import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, message, Typography, Upload, UploadProps } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { TitledRoundedCard } from 'components';
import { MessageType } from 'models/MessageType';
import { hasUploadedFile } from 'components/form';
import { RcFile } from 'antd/es/upload';

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const { Text } = Typography;
const TEN_MB = 10 * 1024 * 1024;
const MAX_PER_FILE_SIZE = TEN_MB;
const MAX_SIZE = TEN_MB * 10;
const MAX_NUMBER_OF_ATTACHMENTS = 12;
type DocumentsCardType = {
  setInvalid: (invalid: boolean) => void;
};
export const DocumentsCard = ({ setInvalid }: DocumentsCardType) => {
  const { t } = useTranslation();

  const [exceedNo, setExceedNo] = useState(false);
  const [exceedSize, setExceedSize] = useState(false);
  const [attachments, setAttachments] = useState<UploadFile[]>([]);
  useEffect(() => {
    setInvalid(exceedSize || exceedNo);
  }, [exceedNo, exceedSize, setInvalid]);

  const attachmentUploadProps: UploadProps = useMemo(
    () => ({
      fileList: attachments,
      listType: 'picture',
      multiple: false,
      accept: 'image/*,application/pdf',
      onChange: ({ fileList }) => {
        const totalSize = fileList.map((a) => a.size || 0).reduce((acc, cur) => acc + cur, 0);
        setExceedSize(totalSize > MAX_SIZE);
        setExceedNo(fileList.length > MAX_NUMBER_OF_ATTACHMENTS);
        setAttachments(fileList);
      },
      beforeUpload: (f) => {
        if (hasUploadedFile(attachments as RcFile[], f)) {
          message[MessageType.ERROR](t('common.error.notAllowUploadSameDocument'));
          return Upload.LIST_IGNORE;
        }

        if (f.size > MAX_PER_FILE_SIZE) {
          message[MessageType.ERROR](t('common.error.sizeExceeds', { amount: 10 }));
          return Upload.LIST_IGNORE;
        }
        // 检查文件类型
        if (f.type.indexOf('image/') !== 0 && f.type !== 'application/pdf') {
          message[MessageType.ERROR](t('common.error.notPdfOrImages'));
          return Upload.LIST_IGNORE;
        }
        return false;
      },
    }),
    [attachments, t],
  );

  return (
    <TitledRoundedCard title={t('enquiry.documentsType')} numbering="2">
      <Form.Item
        name="attachments"
        label={
          <LabelWrapper>
            <Text>{t('enquiry.relatedDocuments')}</Text>
            <Text type="secondary">{t('common.optional')}</Text>
          </LabelWrapper>
        }
      >
        <Upload name="documents" {...attachmentUploadProps}>
          {attachments.length < 12 && <Button icon={<UploadOutlined />}>{t('enquiry.upload')}</Button>}
        </Upload>
      </Form.Item>
      {exceedSize && <Text type="danger">{t('common.error.sizeExceeds', { amount: 100 })}</Text>}
      {exceedNo && <Text type="danger">{t('enquiry.error.noOfFilesExceeds')}</Text>}
    </TitledRoundedCard>
  );
};
